import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
// Eagerly-loaded pages — small, high-frequency landings kept in the main
// bundle so the very first paint has no extra network round-trip.
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";

// Lazy-loaded pages. Each becomes its own JS chunk that only downloads when
// the user actually visits that route. Biggest wins here are the Google-Maps
// backed /map page and the admin dashboard, which together were adding
// hundreds of KB to the initial bundle.
const BrowsePage        = lazy(() => import("@/pages/BrowsePage"));
const ExploreMapPage    = lazy(() => import("@/pages/ExploreMapPage"));
const BookDetailsPage   = lazy(() => import("@/pages/BookDetailsPage"));
const ListBookPage      = lazy(() => import("@/pages/ListBookPage"));
const LedgerPage        = lazy(() => import("@/pages/LedgerPage"));
const ProfilePage       = lazy(() => import("@/pages/ProfilePage"));
const ComingSoon        = lazy(() => import("@/pages/ComingSoon"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AboutUsPage       = lazy(() => import("@/pages/AboutUsPage"));
const ContactPage       = lazy(() => import("@/pages/ContactPage"));
const AdminDashboard    = lazy(() => import("@/pages/AdminDashboard"));
const MessagesPage      = lazy(() => import("@/pages/MessagesPage"));
const ProPlanPage       = lazy(() => import("@/pages/ProPlanPage"));
const FeedbackPage      = lazy(() => import("@/pages/FeedbackPage"));
const ReadingTrackerPage = lazy(() => import("@/pages/ReadingTrackerPage"));
const FaqPage           = lazy(() => import("@/pages/FaqPage"));
const VerifyOtpPage     = lazy(() => import("@/pages/VerifyOtpPage"));

// Simple brand-consistent fallback while a route chunk is downloading.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-9 h-9 rounded-full border-2 border-amber-200 border-t-orange-500 animate-spin"
          role="status"
          aria-label="Loading"
        />
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Loading…</div>
      </div>
    </div>
  );
}

// Scrolls to the very top whenever the pathname changes.
// Also runs on initial load so hard refreshes always start at the top,
// bypassing the browser's native scroll-restoration.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callbacks synchronously (BEFORE ProtectedRoute).
  // - Direct Google flow lands at /auth/callback?code=… (handled by route below)
  // - Legacy Emergent flow used a hash: #session_id=…
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/browse" element={<BrowsePage />} />
          <Route path="/map" element={<ExploreMapPage />} />
          <Route path="/book/:bookId" element={<BookDetailsPage />} />
          <Route path="/list" element={<ProtectedRoute><ListBookPage /></ProtectedRoute>} />
          <Route path="/ledger" element={<ProtectedRoute><LedgerPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/coming-soon" element={<ComingSoon mode="login" />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pro" element={<ProPlanPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/reading-tracker" element={<ProtectedRoute><ReadingTrackerPage /></ProtectedRoute>} />
          <Route path="/faqs" element={<FaqPage />} />
          <Route path="/verify-otp" element={<VerifyOtpPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
}

function App() {
  // Inject JSON-LD structured data into <head>. Done here (not in index.html)
  // so it survives the CRA build and stays in sync with the SPA.
  useEffect(() => {
    const SCRIPT_ID = "jsonld-juniorshelf";
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://juniorshelf.com/#organization",
          "name": "JuniorShelf",
          "url": "https://juniorshelf.com",
          "logo": "https://juniorshelf.com/logo.png",
          "description":
            "A community-focused book-sharing platform that maintains an active ledger of book locations while protecting original ownership.",
          "sameAs": [
            "https://twitter.com/juniorshelf",
            "https://www.linkedin.com/company/juniorshelf",
            "https://www.instagram.com/juniorshelf/"
          ],
        },
        {
          "@type": "WebApplication",
          "@id": "https://juniorshelf.com/#webapp",
          "url": "https://juniorshelf.com",
          "name": "JuniorShelf Platform",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "All",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "INR",
          },
        },
      ],
    });
    document.head.appendChild(s);
  }, []);
  

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
